import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { GlowButton } from "@/components/ui";
import { isSnoozed, MIN_VISITS, snoozeInstall, visitCount } from "./install";
import { useInstall } from "./useInstall";

/** Pages where a pop-up would get in the way of paying or signing in. */
const QUIET = [/^\/checkout/, /^\/payment/, /^\/login/, /^\/register/, /^\/verify/, /^\/reset-password/, /^\/forgot-password/];
const SHOW_AFTER_MS = 4000;

/**
 * "Get the app" card for phones, from the second visit on. Android installs in one tap;
 * iPhone opens the Share, then Add to Home Screen guide. Closing it snoozes it for two weeks.
 */
export function InstallBanner() {
  const { pathname } = useLocation();
  const { platform, available, install } = useInstall();
  const [ready, setReady] = useState(false);
  const [hidden, setHidden] = useState(() => isSnoozed() || visitCount() < MIN_VISITS);

  useEffect(() => {
    const t = window.setTimeout(() => setReady(true), SHOW_AFTER_MS);
    return () => window.clearTimeout(t);
  }, []);

  if (!ready || hidden || !available || QUIET.some((r) => r.test(pathname))) return null;

  const dismiss = () => {
    snoozeInstall();
    setHidden(true);
  };
  const onInstall = async () => {
    await install();
    if (platform === "prompt") setHidden(true);
  };

  return (
    <aside
      aria-label="Install the RealityStore app"
      className="fixed inset-x-3 bottom-[calc(var(--app-bottom)+12px)] z-50 animate-sheet-up rounded-2xl border border-[var(--border-glow)] bg-elevated/95 p-3 shadow-glow-2 backdrop-blur md:hidden"
    >
      <div className="flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="size-12 shrink-0 rounded-xl" width={48} height={48} />
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Get the RealityStore app</p>
          <p className="text-xs leading-snug text-ink-muted">Opens from your home screen, loads faster on weak network.</p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Not now"
          className="-mr-1 -mt-6 grid size-8 shrink-0 place-items-center rounded-full text-ink-muted hover:bg-fg/5 hover:text-ink"
        >
          <X className="size-4" aria-hidden />
        </button>
      </div>
      <div className="mt-3 flex gap-2">
        <GlowButton variant="ghost" size="sm" className="flex-1" onClick={dismiss}>
          Not now
        </GlowButton>
        <GlowButton size="sm" className="flex-1" onClick={() => void onInstall()}>
          {platform === "prompt" ? "Install" : "Show me how"}
        </GlowButton>
      </div>
    </aside>
  );
}
